/**
 * Authentication Service
 * Handles login, logout, token management, and auth state.
 *
 * Real API returns on login:
 *   { access_token, refresh_token, user: { id, first_name, last_name, email, mobile_number, role, ... } }
 *
 * We store:
 *   localStorage.token         → access_token (JWT)
 *   localStorage.refresh_token → refresh_token
 *   localStorage.user          → JSON stringified user object
 */

import { api } from '@crm/shared';

// ─── Token helpers ─────────────────────────────────────────────────────────────
export const getToken = () => localStorage.getItem('token');

export const getRefreshToken = () => localStorage.getItem('refresh_token');

// ─── User helpers ──────────────────────────────────────────────────────────────
export const getUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Returns a display-friendly full name for the logged-in user.
 * Falls back gracefully regardless of which fields the API returned.
 */
export const getUserDisplayName = () => {
  const user = getUser();
  if (!user) return 'User';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.name || user.email?.split('@')[0] || 'User';
};

/**
 * Returns the user's role label.
 * The real API returns role as an integer ID — we can only show it as-is
 * unless a role name was stored alongside it during login.
 */
export const getUserRoleLabel = () => {
  const user = getUser();
  if (!user) return '';
  // If role is an object { id, name }
  if (user.role && typeof user.role === 'object') return user.role.name || '';
  // If role_name was stored separately
  if (user.role_name) return user.role_name;
  // Fallback: just show the role ID (rare, only if API returns integer)
  return user.designation || '';
};

// ─── Auth state ────────────────────────────────────────────────────────────────
export const isAuthenticated = () => {
  return !!(getToken() && getUser());
};

// ─── Store / clear ─────────────────────────────────────────────────────────────
export const storeAuthData = (accessToken, refreshTkn, user) => {
  localStorage.setItem('token',         accessToken);
  localStorage.setItem('user',          JSON.stringify(user));
  if (refreshTkn) localStorage.setItem('refresh_token', refreshTkn);
  console.log('✅ Auth data stored for:', user.email);
};

export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('remember_me');
  console.log('🔓 Auth data cleared');
};

// ─── Login ─────────────────────────────────────────────────────────────────────
export const login = async (email, password, rememberMe = false) => {
  try {
    console.log('🔐 Login attempt:', email);

    const response = await api.login({ email, password });
    const data     = response.data;

    // Real API returns access_token (not token)
    const accessToken  = data.access_token  || data.token  || data.access;
    const refreshToken = data.refresh_token || data.refresh;
    const user         = data.user;

    if (!accessToken || !user) {
      return { success: false, error: 'Login failed — unexpected server response' };
    }

    if (rememberMe) localStorage.setItem('remember_me', 'true');
    storeAuthData(accessToken, refreshToken, user);

    return { success: true, user };
  } catch (err) {
    console.error('❌ Login error:', err);
    const msg =
      err?.detail ||
      err?.message ||
      err?.non_field_errors?.[0] ||
      'Invalid email or password';
    return { success: false, error: msg };
  }
};

// ─── Logout ────────────────────────────────────────────────────────────────────
/**
 * Clears local auth data and dispatches auth:logout so the app can
 * navigate to /login via React Router (avoids "operation is insecure").
 * Pass navigate (from useNavigate) to redirect immediately from a component.
 */
export const logout = (navigate) => {
  clearAuthData();
  if (navigate) {
    navigate('/login', { replace: true });
  } else {
    // Fallback: dispatch custom event so App.js can navigate
    window.dispatchEvent(new Event('auth:logout'));
  }
};

// ─── Token refresh ─────────────────────────────────────────────────────────────
export const refreshAuthToken = async () => {
  try {
    const refresh = getRefreshToken();
    if (!refresh) throw new Error('No refresh token');

    const response = await api.refreshToken(refresh);
    // API returns: { access: "<new-token>" }
    const newToken = response.data?.access || response.data?.token;
    if (!newToken) throw new Error('No access token in refresh response');

    localStorage.setItem('token', newToken);
    console.log('✅ Token refreshed');
    return true;
  } catch (err) {
    console.error('❌ Token refresh failed:', err);
    clearAuthData();
    return false;
  }
};

export const updateUser = (updatedUser) => {
  if (!updatedUser) return;
  localStorage.setItem('user', JSON.stringify(updatedUser));
  window.dispatchEvent(new Event('auth:user-updated'));
};

export default {
  getToken,
  getRefreshToken,
  getUser,
  getUserDisplayName,
  getUserRoleLabel,
  isAuthenticated,
  storeAuthData,
  clearAuthData,
  updateUser,
  login,
  logout,
  refreshAuthToken,
};
